import type { ReactNode } from 'react';
import { usePermission, useAnyPermission } from '@/hooks/usePermission';
import type { PermissionCode } from '@features/shared/constants/permissions';

interface PermissionGuardProps {
  /** Single permission to check */
  permission?: PermissionCode;
  /** Multiple permissions - user needs ANY of these */
  anyOf?: PermissionCode[];
  /** Content to show when user has permission */
  children: ReactNode;
  /** Optional fallback when user lacks permission */
  fallback?: ReactNode;
}

/**
 * Conditional rendering wrapper based on user permissions.
 * Use `permission` for a single check, or `anyOf` to allow any of multiple permissions.
 */
export function PermissionGuard({ permission, anyOf, children, fallback = null }: PermissionGuardProps) {
  const hasSingle = usePermission(permission ?? ('' as PermissionCode));
  const hasAny = useAnyPermission(anyOf ?? []);

  const allowed = permission ? hasSingle : anyOf ? hasAny : false;

  if (!allowed) return <>{fallback}</>;
  return <>{children}</>;
}
